import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import WhatsAppImporter from './WhatsAppImporter';
import QuickWhatsAppImporter from './QuickWhatsAppImporter';

function WhatsAppChat({ userId, userName, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('outgoing');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchMessages();
      fetchTemplates();
    }
  }, [userId]);

  const fetchMessages = async () => {
    try {
      const data = await apiCall(`/users/${userId}/whatsapp`);
      setMessages(data);
      setLoading(false);
    } catch (error) {
      console.error('Fehler beim Laden der Nachrichten:', error);
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await apiCall('/whatsapp/templates');
      setTemplates(data);
    } catch (error) {
      console.error('Fehler beim Laden der Templates:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const response = await apiCall(`/users/${userId}/whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_type: messageType,
          message: newMessage,
          phone_number: null
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(); // Refresh messages
      } else {
        notify('Fehler beim Senden der Nachricht');
      }
    } catch (error) {
      console.error('Fehler:', error);
      notify('Netzwerk-Fehler');
    }
  };

  const applyTemplate = (template) => {
    const personalizedMessage = template.content.replace('{name}', userName || 'Mitarbeiter');
    setNewMessage(personalizedMessage);
    setShowTemplates(false);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'outgoing': return '📤';
      case 'incoming': return '📥';
      case 'note': return '📝';
      default: return '💬';
    }
  };

  const getMessageStyle = (type) => {
    switch (type) {
      case 'outgoing': 
        return {
          backgroundColor: '#dcf8c6',
          marginLeft: '20%',
          borderRadius: '18px 18px 4px 18px'
        };
      case 'incoming': 
        return {
          backgroundColor: '#e5e5ea',
          marginRight: '20%',
          borderRadius: '18px 18px 18px 4px'
        };
      case 'note': 
        return {
          backgroundColor: '#fff2cc',
          border: '1px dashed #d6b656',
          borderRadius: '8px'
        };
      default: 
        return { backgroundColor: '#f0f0f0' };
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Lädt...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center bg-success text-white">
        <div>
          <h5 className="mb-0">💬 WhatsApp Chat - {userName}</h5>
          <small>{messages.length} Nachrichten</small>
        </div>
        <button className="btn btn-outline-light btn-sm" onClick={onClose}>
          ✕ Schließen
        </button>
      </div>

      <div className="card-body p-0">
        {/* Quick PC Import */}
        <QuickWhatsAppImporter 
          userId={userId}
          userName={userName}
          onImportComplete={fetchMessages}
        />
        
        {/* WhatsApp File Import */}
        <WhatsAppImporter 
          userId={userId}
          userName={userName}
          onImportComplete={fetchMessages}
        />
        
        {/* Messages */}
        <div 
          className="overflow-auto p-3" 
          style={{ height: '400px', backgroundColor: '#f5f5f5' }}
        >
          {messages.length === 0 ? (
            <div className="text-center text-muted py-5">
              <div style={{ fontSize: '48px' }}>📱</div>
              <p>Noch keine Nachrichten vorhanden</p>
              <p>Beginne das Gespräch mit einer Vorlage!</p>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className="mb-3">
                <div 
                  className="p-3"
                  style={getMessageStyle(msg.message_type)}
                >
                  <div className="d-flex align-items-start gap-2">
                    <span style={{ fontSize: '16px' }}>{getMessageIcon(msg.message_type)}</span>
                    <div className="flex-grow-1">
                      <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                        {msg.message}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                        {formatTimestamp(msg.timestamp)}
                        {msg.message_type === 'outgoing' && (
                          <span className="ms-2">
                            {msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓' : '⏳'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Templates Panel */}
        {showTemplates && (
          <div className="border-top">
            <div className="p-3 bg-light">
              <h6 className="mb-2">📄 Nachrichtenvorlagen</h6>
              <div className="row">
                {templates.map(template => (
                  <div key={template.id} className="col-md-6 mb-2">
                    <button
                      className="btn btn-outline-primary btn-sm w-100 text-start"
                      onClick={() => applyTemplate(template)}
                      style={{ fontSize: '12px' }}
                    >
                      <strong>{template.name}</strong>
                      <br />
                      <small className="text-muted">
                        {template.content.substring(0, 50)}...
                      </small>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-top">
          <div className="p-3">
            {/* Message Type Selection */}
            <div className="mb-2">
              <div className="btn-group btn-group-sm" role="group">
                <input 
                  type="radio" 
                  className="btn-check" 
                  id="outgoing" 
                  value="outgoing"
                  checked={messageType === 'outgoing'}
                  onChange={(e) => setMessageType(e.target.value)}
                />
                <label className="btn btn-outline-success" htmlFor="outgoing">
                  📤 Gesendet
                </label>

                <input 
                  type="radio" 
                  className="btn-check" 
                  id="incoming" 
                  value="incoming"
                  checked={messageType === 'incoming'}
                  onChange={(e) => setMessageType(e.target.value)}
                />
                <label className="btn btn-outline-primary" htmlFor="incoming">
                  📥 Erhalten
                </label>

                <input 
                  type="radio" 
                  className="btn-check" 
                  id="note" 
                  value="note"
                  checked={messageType === 'note'}
                  onChange={(e) => setMessageType(e.target.value)}
                />
                <label className="btn btn-outline-warning" htmlFor="note">
                  📝 Notiz
                </label>
              </div>
            </div>

            {/* Message Input */}
            <div className="input-group">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowTemplates(!showTemplates)}
                title="Vorlagen anzeigen"
              >
                📄
              </button>
              <textarea
                className="form-control"
                placeholder="Nachricht eingeben..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows="3"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    sendMessage();
                  }
                }}
              />
              <button
                className="btn btn-success"
                onClick={sendMessage}
                disabled={!newMessage.trim()}
              >
                Senden
              </button>
            </div>
            <small className="text-muted">Tipp: Strg+Enter zum Senden</small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppChat;
